import { type NotificationConfigProps } from "@snapchat/snap-design-system";

import { GRPC_ERROR_CODES } from "./errorCodes";

export enum GraphQLApiPaths {
    CreateLensSpaceLens = "createLensSpaceLens",
    SetLensCta = "setLensCta",
    SetLensPreview = "setLensPreview",
    CreateOrganizationAndLensSpace = "createOrganizationAndLensSpace",
    CreateLensSpace = "createLensSpace",
}

export const createRateLimitNotificationError = (path: string | number): NotificationConfigProps => {
    const title = "Please try again later.";
    switch (path) {
        case GraphQLApiPaths.CreateLensSpaceLens:
            return {
                title,
                message:
                    "You have reached the maximum number of Lens submission attempts allowed for a given time period.",
            };
        case GraphQLApiPaths.SetLensCta:
        case GraphQLApiPaths.SetLensPreview:
            return {
                title,
                message:
                    "You have reached the maximum number of Lens updates attempts allowed for a given time period.",
            };
        default:
            return {
                title,
                message: "You have reached the maximum number of Lens actions allowed for a given period.",
            };
    }
};

export const createInternalNotificationError = (path: string | number): NotificationConfigProps | null => {
    switch (path) {
        case GraphQLApiPaths.CreateLensSpaceLens:
            return {
                title: "Lens Submission Error",
                message: "Please try again later.",
            };
    }
    return null;
};

export const createInvalidArgumentNotificationError = (path: string | number): NotificationConfigProps | null => {
    const message = "Please try again later.";

    switch (path) {
        case GraphQLApiPaths.CreateOrganizationAndLensSpace:
            return {
                title: "Lens Organization and Folder Creation Error",
                message,
            };
        case GraphQLApiPaths.CreateLensSpace:
            return {
                title: "Lens Folder Creation Error",
                message,
            };
    }

    return null;
};

const COMMUNITY_DISTRIBUTION_DENIED_REGEX =
    /7 PERMISSION_DENIED: User (\S)+ is not authorized to distribute lens ([\S ])+ to community destination/;

export const isCommunityDistributionPermissionDenied = (message = ""): boolean =>
    COMMUNITY_DISTRIBUTION_DENIED_REGEX.test(message);

export const createPermissionDeniedError = (message = ""): NotificationConfigProps | null => {
    if (isCommunityDistributionPermissionDenied(message)) {
        return {
            title: "Permission Denied",
            message: 'You don’t have access to publish to the selected organization. Please use "Save as Draft".',
        };
    }

    return {
        title: "Permission Denied",
    };
};

// TODO: review the notification system
export const createNotificationError = (
    path: string | number,
    errorCode: number,
    message?: string,
): NotificationConfigProps | null => {
    if (errorCode === GRPC_ERROR_CODES.RESOURCE_EXHAUSTED && message?.includes("RESOURCE_EXHAUSTED")) {
        return createRateLimitNotificationError(path);
    }

    switch (errorCode) {
        case GRPC_ERROR_CODES.INVALID_ARGUMENT: {
            return createInvalidArgumentNotificationError(path);
        }
        case GRPC_ERROR_CODES.INTERNAL: {
            return createInternalNotificationError(path);
        }
        case GRPC_ERROR_CODES.PERMISSION_DENIED: {
            return createPermissionDeniedError(message);
        }
        default:
            return null;
    }
};
